import { z } from "zod";
import { courierProviderSchema, courierStatusSchema } from "./couriers.js";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** COD reconciliation (S-04, B-39; docs/specs/shipping.md): courier payouts matched to shipments. */
/** `draft` (imported and matched, nothing posted), `posted` (money recorded, parcels settled), `void` (discarded). */
export const PAYOUT_STATUSES = ["draft", "posted", "void"];
export const payoutStatusSchema = z.enum(PAYOUT_STATUSES);
/**
 * One payout line against our shipments: `matched` (the amount is what we expected), `mismatch` (a parcel of ours, but
 * the amount or its state is off), `unmatched` (no parcel of this account has that code), `duplicate` (the parcel is
 * already settled, or twice in this payout).
 */
export const MATCH_STATUSES = ["matched", "mismatch", "unmatched", "duplicate"];
export const matchStatusSchema = z.enum(MATCH_STATUSES);
export const MAX_PAYOUT_LINES = 5000;
export const payoutLineInputSchema = z
    .object({
    /** The courier's consignment ID or tracking code; else our invoice must be given. */
    consignmentId: z.string().trim().min(1).max(100).nullable().default(null),
    invoice: z.string().trim().min(1).max(100).nullable().default(null),
    /** What the courier collected from the customer. */
    collectedPaisa: paisa().min(0),
    /** Everything the courier kept for this parcel (delivery charge, COD fee, return charge). */
    chargePaisa: paisa().min(0).default(0),
})
    .strict()
    .refine((line) => line.consignmentId !== null || line.invoice !== null, {
    message: "Give the consignment ID or the invoice",
    path: ["consignmentId"],
});
/** `POST /v1/courier-payouts`: a payout from the courier's statement (CSV parsed by the client, or typed). */
export const importPayoutRequestSchema = z
    .object({
    courierAccountId: z.uuid(),
    /** The courier's payout or invoice number; unique per courier account. */
    reference: z.string().trim().min(1).max(100),
    paidAt: z.iso.date(),
    /** What arrived in the bank or wallet; compared with the lines (collected − charges). */
    totalPaisa: paisa().min(0),
    note: z.string().trim().max(500).default(""),
    lines: z.array(payoutLineInputSchema).min(1).max(MAX_PAYOUT_LINES),
})
    .strict();
export const payoutLineSchema = z.object({
    id: z.uuid(),
    payoutId: z.uuid(),
    payoutReference: z.string(),
    courierAccountId: z.uuid(),
    consignmentId: z.string().nullable(),
    invoice: z.string().nullable(),
    collectedPaisa: paisa(),
    chargePaisa: paisa(),
    /** What we expected the courier to collect (null: no parcel, or any amount up to the COD for a partial delivery). */
    expectedPaisa: paisa().nullable(),
    matchStatus: matchStatusSchema,
    reason: z.string(),
    shipmentId: z.uuid().nullable(),
    orderId: z.uuid().nullable(),
    orderNumber: z.string().nullable(),
    courierStatus: courierStatusSchema.nullable(),
    createdAt: z.iso.datetime(),
});
export const payoutSchema = z.object({
    id: z.uuid(),
    courierAccountId: z.uuid(),
    provider: courierProviderSchema,
    reference: z.string(),
    paidAt: z.iso.date(),
    status: payoutStatusSchema,
    totalPaisa: paisa(),
    /** Σ collected − Σ charges over the lines; `differencePaisa` = total − this (0 when the statement adds up). */
    collectedPaisa: paisa(),
    chargesPaisa: paisa(),
    differencePaisa: paisa(),
    counts: z.object({
        matched: z.int(),
        mismatch: z.int(),
        unmatched: z.int(),
        duplicate: z.int(),
    }),
    paymentAccountId: z.uuid().nullable(),
    postedAt: z.iso.datetime().nullable(),
    note: z.string(),
    version: z.int(),
    createdAt: z.iso.datetime(),
});
export const payoutDetailSchema = payoutSchema.extend({ lines: z.array(payoutLineSchema) });
export const payoutListQuerySchema = z.object({
    courierAccountId: z.uuid().optional(),
    status: payoutStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const payoutListSchema = z.object({ data: z.array(payoutSchema), nextCursor: z.uuid().nullable() });
/** The mismatch report: lines of draft and posted payouts that did not match (or one status). */
export const payoutLineListQuerySchema = z.object({
    matchStatus: matchStatusSchema.optional(),
    courierAccountId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const payoutLineListSchema = z.object({
    data: z.array(payoutLineSchema),
    nextCursor: z.uuid().nullable(),
});
/** Posting: the payout's money goes into this payment account (bank or wallet the courier pays to). */
export const postPayoutRequestSchema = z
    .object({ paymentAccountId: z.uuid(), version: z.int().min(1) })
    .strict();
export const voidPayoutRequestSchema = z
    .object({ reason: z.string().trim().min(1).max(250), version: z.int().min(1) })
    .strict();
/** S-04 "money held by courier", per courier account. */
export const codSummarySchema = z.object({
    data: z.array(z.object({
        courierAccountId: z.uuid(),
        name: z.string(),
        provider: courierProviderSchema,
        /** Collected from delivered parcels and not yet paid out (posted payouts settle parcels). */
        heldPaisa: paisa(),
        heldParcels: z.int(),
        /** COD of booked parcels still on their way. */
        inTransitPaisa: paisa(),
        inTransitParcels: z.int(),
    })),
});
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "shipping" };
const view = { permission: "cod.view" };
const reconcile = { permission: "cod.reconcile" };
export const getCodSummary = defineEndpoint({
    ...tag,
    name: "getCodSummary",
    method: "GET",
    path: "/v1/cod/summary",
    access: view,
    summary: "COD held by each courier (delivered, not paid out) and still on its way",
    status: 200,
    response: codSummarySchema,
});
export const listPayouts = defineEndpoint({
    ...tag,
    name: "listPayouts",
    method: "GET",
    path: "/v1/courier-payouts",
    access: view,
    summary: "Courier payouts, newest first",
    query: payoutListQuerySchema,
    status: 200,
    response: payoutListSchema,
});
export const getPayout = defineEndpoint({
    ...tag,
    name: "getPayout",
    method: "GET",
    path: "/v1/courier-payouts/{id}",
    access: view,
    summary: "One payout with its lines and how each matched",
    params: idParamsSchema,
    status: 200,
    response: payoutDetailSchema,
});
export const importPayout = defineEndpoint({
    ...tag,
    name: "importPayout",
    method: "POST",
    path: "/v1/courier-payouts",
    access: reconcile,
    summary: "Import a courier payout (draft); every line is matched to our parcels",
    body: importPayoutRequestSchema,
    idempotent: true,
    status: 201,
    response: payoutDetailSchema,
    errors: ["PAYOUT_EXISTS"],
});
export const postPayout = defineEndpoint({
    ...tag,
    name: "postPayout",
    method: "POST",
    path: "/v1/courier-payouts/{id}/post",
    access: reconcile,
    summary: "Record the payout's money in a payment account and settle its parcels",
    params: idParamsSchema,
    body: postPayoutRequestSchema,
    idempotent: true,
    status: 200,
    response: payoutDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION", "ACCOUNT_ARCHIVED"],
});
export const voidPayout = defineEndpoint({
    ...tag,
    name: "voidPayout",
    method: "POST",
    path: "/v1/courier-payouts/{id}/void",
    access: reconcile,
    summary: "Discard a draft payout (to import a corrected one)",
    params: idParamsSchema,
    body: voidPayoutRequestSchema,
    status: 200,
    response: payoutDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION"],
});
export const listPayoutLines = defineEndpoint({
    ...tag,
    name: "listPayoutLines",
    method: "GET",
    path: "/v1/courier-payout-lines",
    access: view,
    summary: "Mismatch report: payout lines that did not match (draft and posted payouts)",
    query: payoutLineListQuerySchema,
    status: 200,
    response: payoutLineListSchema,
});
//# sourceMappingURL=cod.js.map